import { useEffect, useState } from 'react'
import { resourceService } from '../services'
import {
  DocumentTextIcon,
  BookOpenIcon,
  PresentationChartLineIcon,
  ArrowDownTrayIcon,
  SparklesIcon,
  CodeBracketIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline'

export default function Resources() {
  const [resources, setResources] = useState([])
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(null)

  useEffect(() => {
    fetchResources()
  }, [])

  const fetchResources = async () => {
    try {
      setLoading(true)
      const data = await resourceService.getAll()
      setResources(data)
    } catch (err) {
      console.error('Failed to load resources:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = async (item) => {
    try {
      setDownloading(item.id)
      const response = await resourceService.download(item.id)
      
      // Determine filename from response or item
      let filename = 'download'
      
      // Try to get filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition']
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
        if (filenameMatch) {
          filename = filenameMatch[1]
        }
      }
      
      // Fallback to item properties
      if (filename === 'download') {
        if (item.file_path) {
          filename = item.file_path.split('/').pop()
        } else if (item.title) {
          filename = item.title.replace(/[^a-z0-9]/gi, '-').toLowerCase() + '.zip'
        }
      }
      
      // Create blob and download
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Download failed:', err)
      const errorMsg = err.response?.data?.message || 'Failed to download resource. Please try again.'
      alert(errorMsg)
    } finally {
      setDownloading(null)
    }
  }

  const handleView = async (item) => {
    try {
      const data = await resourceService.view(item.id)
      
      if (data.type === 'markdown') {
        // Open markdown content in a new window with basic styling
        const newWindow = window.open('', '_blank')
        newWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>${item.title}</title>
              <style>
                body {
                  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                  max-width: 800px;
                  margin: 0 auto;
                  padding: 2rem;
                  line-height: 1.6;
                }
                pre { background: #f5f5f5; padding: 1rem; border-radius: 4px; overflow-x: auto; }
                code { background: #f5f5f5; padding: 0.2rem 0.4rem; border-radius: 3px; }
                h1, h2, h3 { color: #2563EB; }
              </style>
            </head>
            <body>
              <h1>${item.title}</h1>
              <pre>${data.content}</pre>
            </body>
          </html>
        `)
        newWindow.document.close()
      }
    } catch (err) {
      console.error('View failed:', err)
      alert('Failed to view resource. Please try again.')
    }
  }

  const getCategoryIcon = (category) => {
    const icons = {
      'Prompt Templates': SparklesIcon,
      'Lab Starter Files': CodeBracketIcon,
      'Worksheets': DocumentTextIcon,
      'Rubrics & Guidelines': ClipboardDocumentCheckIcon,
      'Presentation Slides': PresentationChartLineIcon,
      'Quick Reference': BookOpenIcon,
    }
    return icons[category] || BookOpenIcon
  }

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-50 border-blue-200 text-blue-600',
      green: 'bg-green-50 border-green-200 text-green-600',
      purple: 'bg-purple-50 border-purple-200 text-purple-600',
      orange: 'bg-orange-50 border-orange-200 text-orange-600',
      red: 'bg-red-50 border-red-200 text-red-600',
    }
    return colors[color] || colors.blue
  }

  const getIconBgColor = (color) => {
    const colors = {
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      purple: 'bg-purple-500',
      orange: 'bg-orange-500',
      red: 'bg-red-500',
    }
    return colors[color] || colors.blue
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cte-blue mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading resources...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-cte-blue to-blue-700 rounded-2xl p-8 text-white">
        <div className="flex items-center mb-4">
          <BookOpenIcon className="h-10 w-10 mr-3" />
          <h1 className="text-4xl font-bold">Resources Library</h1>
        </div>
        <p className="text-blue-100 text-lg max-w-3xl">
          Templates, worksheets, and references to accelerate your AI agent development.
          Download and customize these resources for your projects.
        </p>
      </div>

      {/* Resources by Category */}
      {resources.map((category) => {
        const Icon = getCategoryIcon(category.category)
        return (
          <div key={category.category} className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <div className={`${getIconBgColor(category.color)} rounded-lg p-2 mr-3`}>
                <Icon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">{category.category}</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {category.items.map((item) => (
                <div
                  key={item.id}
                  className={`border-2 rounded-lg p-4 hover:shadow-md transition-shadow ${getColorClasses(
                    category.color
                  )}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-gray-900">{item.title}</h3>
                    <span className="text-xs font-medium px-2 py-1 bg-white rounded border border-gray-200 text-gray-600">
                      {item.type}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{item.description}</p>
                  {item.formats && (
                    <div className="flex gap-2 mb-3">
                      {item.formats.map((format) => (
                        <span
                          key={format}
                          className="text-xs bg-white px-2 py-1 rounded border border-gray-200"
                        >
                          {format}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDownload(item)}
                      disabled={downloading === item.id}
                      className="text-sm font-medium text-cte-blue hover:text-blue-700 flex items-center disabled:opacity-50"
                    >
                      {downloading === item.id ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-cte-blue mr-1"></div>
                          Downloading...
                        </>
                      ) : (
                        <>
                          <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                          {item.source === 'lab' ? 'Download ZIP' : 'Download'}
                        </>
                      )}
                    </button>
                    {item.source !== 'lab' && (
                      <button
                        onClick={() => handleView(item)}
                        className="text-sm font-medium text-gray-600 hover:text-gray-800"
                      >
                        View
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      })}

      {/* Help Section */}
      <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Need More Resources?</h3>
        <p className="text-gray-600 mb-4">
          Additional resources, code examples, and templates are included in the course modules.
          Check the relevant lesson for context-specific materials.
        </p>
        <p className="text-sm text-gray-500">
          💡 <strong>Tip:</strong> Bookmark this page for quick access to templates and references while building your projects.
        </p>
      </div>
    </div>
  )
}
